/**
 * @name ThemeTransition
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Componente de transição temática com efeito de ondas inspirado no tema do clube de praia
 * @returns {JSX.Element} - JSX do componente de transição
 * @package src/components/common/transitions/ThemeTransition.tsx
 */

import { Box, Fade } from "@mui/material";
import React from "react";

interface ThemeTransitionProps {
  isTransitioning: boolean;
  isDarkMode: boolean;
}

const ThemeTransition: React.FC<ThemeTransitionProps> = ({
  isTransitioning,
  isDarkMode,
}) => {
  if (!isTransitioning) return null;

  return (
    <Fade in={isTransitioning} timeout={300}>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        {/* Efeito de ondas */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: "-50%",
            width: "200%",
            height: "100%",
            background: isDarkMode
              ? "linear-gradient(180deg, transparent 0%, rgba(26, 35, 126, 0.1) 30%, rgba(26, 35, 126, 0.3) 70%, rgba(26, 35, 126, 0.8) 100%)"
              : "linear-gradient(180deg, transparent 0%, rgba(255, 152, 0, 0.1) 30%, rgba(255, 152, 0, 0.3) 70%, rgba(255, 152, 0, 0.8) 100%)",
            borderRadius: "50% 50% 0 0",
            transform: "translateY(50%)",
            animation: "waveTransition 0.6s ease-in-out forwards",
            "@keyframes waveTransition": {
              "0%": {
                transform: "translateY(100%) scale(0.8)",
                opacity: 0,
              },
              "50%": {
                transform: "translateY(30%) scale(1.1)",
                opacity: 0.8,
              },
              "100%": {
                transform: "translateY(50%) scale(1)",
                opacity: 0,
              },
            },
          }}
        />

        {/* Partículas flutuantes */}
        {[...Array(8)].map((_, index) => (
          <Box
            key={index}
            sx={{
              position: "absolute",
              width: "4px",
              height: "4px",
              background: isDarkMode ? "#ffffff" : "#1976d2",
              borderRadius: "50%",
              left: `${20 + index * 10}%`,
              top: `${30 + (index % 3) * 20}%`,
              animation: `floatParticle${index} 0.8s ease-out forwards`,
              opacity: 0,
              [`@keyframes floatParticle${index}`]: {
                "0%": {
                  transform: "translateY(20px) scale(0)",
                  opacity: 0,
                },
                "30%": {
                  transform: "translateY(-10px) scale(1.2)",
                  opacity: 0.8,
                },
                "70%": {
                  transform: "translateY(-30px) scale(0.8)",
                  opacity: 0.4,
                },
                "100%": {
                  transform: "translateY(-50px) scale(0)",
                  opacity: 0,
                },
              },
              animationDelay: `${index * 0.1}s`,
            }}
          />
        ))}
      </Box>
    </Fade>
  );
};

export default ThemeTransition;
